import { getValue, setValue } from 'utils/indexedDB';

export const CHANGE_EVENT = 'heltar:test-widget-changed';

const ACTIVE_USER_KEY = 'heltar:test-widget-user';
const STORE = 'web_widget_test_users';

export const DEFAULT_HEADER_TITLE = 'Widget test';
export const DEFAULT_HEADER_SUBTITLE =
  'Messages land in your inbox under "Test (…)"';

export interface TestWidgetUser {
  id: string;
  name: string;
  createdAt: number;
}

export type ActiveTestUser = Pick<TestWidgetUser, 'id' | 'name'>;

const NONCE_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';
const NONCE_LEN = 12;

export const freshTestNonce = (): string => {
  let out = '';
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c?.getRandomValues) {
    const buf = new Uint32Array(NONCE_LEN);
    c.getRandomValues(buf);
    for (let i = 0; i < NONCE_LEN; i++)
      out += NONCE_ALPHABET[buf[i] % NONCE_ALPHABET.length];
  } else {
    for (let i = 0; i < NONCE_LEN; i++)
      out += NONCE_ALPHABET[Math.floor(Math.random() * NONCE_ALPHABET.length)];
  }
  return out;
};

export const makeTestVisitorId = (
  employeeId: number | string,
  nonce: string = freshTestNonce(),
): string => `test-${employeeId}-${nonce}`;

export const belongsToEmployee = (
  id: string | undefined | null,
  employeeId: number | string,
): boolean => {
  if (!id) return false;
  const own = `test-${employeeId}`;
  return id === own || id.startsWith(`${own}-`);
};

export const resolveTesterIdentity = (
  name: string | undefined,
  employeeName: string | undefined,
): { visitorName: string; headerTitle: string } => {
  const trimmed = (name ?? '').trim();
  if (trimmed) return { visitorName: trimmed, headerTitle: trimmed };
  return {
    visitorName: `Test (${employeeName ?? 'Admin'})`,
    headerTitle: DEFAULT_HEADER_TITLE,
  };
};

const usersKey = (businessId: number | string, employeeId: number | string) =>
  `${businessId}:${employeeId}`;

export const loadTestUsers = async (
  businessId: number | string,
  employeeId: number | string,
): Promise<TestWidgetUser[]> =>
  (await getValue<TestWidgetUser[]>(STORE, usersKey(businessId, employeeId))) ??
  [];

export const saveTestUsers = (
  businessId: number | string,
  employeeId: number | string,
  users: TestWidgetUser[],
): Promise<void> => setValue(STORE, usersKey(businessId, employeeId), users);

export const getActiveTestUser = (): ActiveTestUser | null => {
  try {
    const raw = localStorage.getItem(ACTIVE_USER_KEY);
    return raw ? (JSON.parse(raw) as ActiveTestUser) : null;
  } catch {
    return null;
  }
};

export const setActiveTestUser = (user: ActiveTestUser | null): void => {
  try {
    if (user) localStorage.setItem(ACTIVE_USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(ACTIVE_USER_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

export const ensureActiveTestUser = async (
  businessId: number,
  employeeId: number,
): Promise<ActiveTestUser> => {
  const current = getActiveTestUser();
  if (current && belongsToEmployee(current.id, employeeId)) return current;

  const users = await loadTestUsers(businessId, employeeId);
  const reusable = users.find(u => belongsToEmployee(u.id, employeeId));
  if (reusable) {
    const active = { id: reusable.id, name: reusable.name };
    setActiveTestUser(active);
    return active;
  }

  const fresh: TestWidgetUser = {
    id: makeTestVisitorId(employeeId),
    name: '',
    createdAt: Date.now(),
  };
  await saveTestUsers(businessId, employeeId, [...users, fresh]);
  const active = { id: fresh.id, name: fresh.name };
  setActiveTestUser(active);
  return active;
};
